import { FilterAltOutlined } from '@mui/icons-material'
import {
  Badge,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Popover,
  Stack,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'

type IScenario = { name: string; selected: boolean }

type ISelectScenarios = {
  allScenarios: string[]
  visibleScenarios: string[]
  updateScenarios: Function
  color?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning'
  size?: 'small' | 'medium'
  badgeColor?:
    | 'primary'
    | 'secondary'
    | 'default'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
}

export const SelectScenarios: React.FC<ISelectScenarios> = ({
  allScenarios,
  visibleScenarios,
  updateScenarios,
  size,
  color,
  badgeColor,
}) => {
  const [selectedScenarios, setSelectedScenarios] = useState<IScenario[]>([])
  const [isSelectedAllScenarios, setIsSelectedAllScenarios] = useState(false)
  const [isAllSelected, setIsAllSelected] = useState(false)

  const updateSelectedScenarios = (value: boolean) => {
    const scenarios = [...selectedScenarios]
    scenarios.forEach((sc: IScenario) => {
      sc.selected = value
    })
    setSelectedScenarios(scenarios)
  }

  useEffect(() => {
    const allScenarioNotSelected = selectedScenarios.filter(
      (sc: IScenario) => sc.selected === false,
    )
    if (allScenarioNotSelected.length === allScenarios.length) {
      setIsSelectedAllScenarios(false)
    } else {
      setIsSelectedAllScenarios(true)
    }
    const isAllNotSelected = selectedScenarios.find(
      (sc: IScenario) => sc.selected !== true,
    )
    if (isAllNotSelected) {
      setIsAllSelected(false)
    } else {
      setIsAllSelected(true)
    }
  }, [selectedScenarios])

  useEffect(() => {
    if (isAllSelected) {
      updateSelectedScenarios(isAllSelected)
    }
  }, [isAllSelected])

  useEffect(() => {
    const scenarios: IScenario[] = allScenarios?.map(
      (sc: string): IScenario => ({
        name: sc,
        selected: visibleScenarios?.includes(sc),
      }),
    )
    setSelectedScenarios(scenarios)
  }, [allScenarios, visibleScenarios])

  const toggleSelect = (scenario: IScenario) => {
    const scenarios = [...selectedScenarios]
    const scenarioItem = scenarios.find(
      (sc: IScenario) => sc.name === scenario.name,
    )
    if (scenarioItem) {
      scenarioItem.selected = !scenario.selected
      setSelectedScenarios(scenarios)
    }
  }

  const handleApplyChange = () => {
    const updatedScenarios = selectedScenarios?.filter(
      (sc: IScenario) => sc.selected,
    )
    const updatedScenarioNames = updatedScenarios?.map(
      (sc: IScenario) => sc.name,
    )
    updateScenarios(updatedScenarioNames)
    setAnchorEl(null)
  }

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    updateScenarios(visibleScenarios)
    setSelectedScenarios(
      allScenarios?.map(
        (sc: string): IScenario => ({
          name: sc,
          selected: visibleScenarios?.includes(sc),
        }),
      ),
    )
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'select-scenarios' : undefined

  const getSelectedScenariosCount = () =>
    selectedScenarios?.filter((s: IScenario) => s.selected).length

  const toggleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsAllSelected(event.target.checked)
    updateSelectedScenarios(event.target.checked)
  }

  return (
    <>
      <Button
        disableFocusRipple
        disableRipple
        variant='text'
        size={size || 'small'}
        color={color || 'primary'}
        aria-describedby={id}
        endIcon={
          <Badge
            badgeContent={getSelectedScenariosCount()}
            color={badgeColor || 'primary'}
            sx={{
              fontSize: '0.75rem',
            }}
          >
            <FilterAltOutlined />
          </Badge>
        }
        onClick={handleClick}
      >
        Scenarios
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Card sx={{ width: '20rem' }}>
          <CardHeader
            title={
              <Typography variant='h5'>Select Visible Scenarios</Typography>
            }
            sx={{ pb: 2 }}
          />
          <Divider />
          <CardContent sx={{ pt: 2, pb: 0 }}>
            <FormGroup>
              <FormControlLabel
                label='(Select all)'
                control={
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isAllSelected !== isSelectedAllScenarios}
                    onChange={toggleSelectAll}
                    size='small'
                  />
                }
              />
              {selectedScenarios?.map((scenario: IScenario) => {
                return (
                  <FormControlLabel
                    key={scenario.name}
                    control={
                      <Checkbox
                        checked={scenario.selected}
                        onChange={() => toggleSelect(scenario)}
                        size='small'
                      />
                    }
                    label={scenario.name}
                  />
                )
              })}
            </FormGroup>
          </CardContent>
          <Divider />
          <CardActions>
            <Stack
              direction='row'
              width='100%'
              justifyContent='flex-end'
              gap={4}
            >
              <Button color='primary' variant='outlined' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                color='primary'
                variant='contained'
                onClick={handleApplyChange}
                disabled={
                  !selectedScenarios.find(
                    (sc: IScenario) => sc.selected === true,
                  )
                }
              >
                Apply Changes
              </Button>
            </Stack>
          </CardActions>
        </Card>
      </Popover>
    </>
  )
}
